import { Fragment, type FC } from 'react'
import { Divider } from 'antd'
import dayjs from 'dayjs'

import { UtilService } from 'common/services/utilService'
import { ALT_CONSTANTS } from 'common/constants/altConstants'

import { CartCollapse } from 'features/Cart/components/CartCollapse/CartCollapse'
import { CartError } from 'features/Cart/components/CartError/CartError'
import type { IOptionShopRoom } from 'features/Option'
import trashIcon from 'features/Cart/assets/trash.svg'

import styles from './cartShopItem.module.scss'
import collapseStyles from 'features/Cart/components/CartCollapse/cartCollapse.module.scss'
import classNames from 'classnames'

interface IProps {
  rooms: IOptionShopRoom[]
  handleRemoveSlot: (roomId: number, slotId: number, day?: string) => void
  handleRemoveSlotsForDate?: (date: string) => void
  handleRemoveRoom?: (roomId: number) => void
}

export const CartShopRooms: FC<IProps> = ({
  rooms,
  handleRemoveSlot,
  handleRemoveSlotsForDate,
  handleRemoveRoom,
}) => {
  return (
    <>
      {rooms?.map(({ room, selectedSlots, error_message }: IOptionShopRoom) => {
        if (!selectedSlots || selectedSlots.length === 0) return null

        const slotsByDate = selectedSlots.reduce((acc, slot) => {
          const date = slot.date
          if (!acc[date]) {
            acc[date] = []
          }
          acc[date].push(slot)
          return acc
        }, {} as Record<string, typeof selectedSlots>)

        return (
          <CartCollapse
            key={room.code}
            title={`${room.name} - ${selectedSlots.length} Time Slots Selected`}
            handleRemoveSlot={() => {
              if (handleRemoveRoom) {
                handleRemoveRoom(room.code as number)
              } else {
                selectedSlots.forEach((slot) => handleRemoveSlot(room.code as number, slot.id))
              }
            }}
            error={error_message}
            additionalActiveTitle={`${selectedSlots.length} slots`}>
            <div>
              {Object.entries(slotsByDate).map(([date, dateSlots]) => (
                <div key={date} className={styles.dateSection}>
                  <div className={styles.dateHeader}>
                    <span>
                      {dayjs(date).format("ddd, MMM DD 'YY")} ({dateSlots.length} slots)
                    </span>
                    <button
                      className={collapseStyles.parentRemoveButton}
                      onClick={() => {
                        if (handleRemoveSlotsForDate) {
                          handleRemoveSlotsForDate(date)
                        } else {
                          dateSlots.forEach((slot) =>
                            handleRemoveSlot(room.code as number, slot.id),
                          )
                        }
                      }}
                      title='Remove all slots for this day'
                      data-cy={`delete-room-slots-${date}`}>
                      <img
                        src={trashIcon}
                        alt={ALT_CONSTANTS.ACTION_ICON}
                        className={collapseStyles.parentRoomRemoveIcon}
                      />
                    </button>
                  </div>
                  {dateSlots.map((slot) => (
                    <div key={slot.id} className={styles.divider}>
                      <div key={slot.id} className={classNames(styles.parent, styles.slotItem)}>
                        <div>
                          {slot.title}
                          {slot.quantity && ` (Qty: ${slot.quantity})`}
                        </div>
                        <span>
                          {slot.quantity
                            ? UtilService.numberToDollar(slot.quantity * slot.price)
                            : UtilService.numberToDollar(slot.price)}
                        </span>
                      </div>
                      <CartError error={slot?.error_message} className={styles.slotItem} />
                    </div>
                  ))}
                  <div className={`${styles.parent} ${styles.dayTotal}`}>
                    <span>Day Total</span>
                    <span>
                      {UtilService.numberToDollar(
                        dateSlots.reduce(
                          (sum, s) => sum + (s.quantity ? s.quantity * s.price : s.price),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                </div>
              ))}
              <Divider className={styles.divider} />
              <div className={classNames(styles.parent, styles.grandTotal)}>
                <span>Grand Total</span>
                <span>
                  {UtilService.numberToDollar(
                    selectedSlots.reduce(
                      (sum, s) => sum + (s.quantity ? s.quantity * s.price : s.price),
                      0,
                    ),
                  )}
                </span>
              </div>
            </div>
          </CartCollapse>
        )
      })}
    </>
  )
}
